import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Field, Textarea } from '@/components/ui/field';
import { EmptyState } from '@/components/ui/states';
import { useUpdateBrandStandardsMutation, type BrandDetail } from '../brandsApi';
import { messageOf } from '../lib';

/**
 * ADR-0023 §3 — the brand standards/configuration document. Free-form and
 * versionless BY DESIGN (`Brand.standards`'s own Prisma comment): unlike a
 * menu publish it does not change what a guest is charged, so it does not
 * need ADR-0011's draft/review/publish machinery — `updatedAt` plus the
 * audit trail (`brand.standards_update` is `@Audit`-ed server-side) is
 * enough to answer "what changed and when."
 *
 * Edited as raw JSON rather than a generated form: the document's shape is
 * whatever a franchisor decides it is (recipe steps, service scripts,
 * presentation photos, anything), and `UpdateBrandStandardsDto` accepts any
 * object — inventing a schema here would either lag what the franchisor
 * actually wants to record or invent one nobody asked for.
 */
export default function StandardsPanel({ brand }: { brand: BrandDetail }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canEdit = can('brands.manage_standards');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => JSON.stringify(brand.standards ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [save, { isLoading: saving }] = useUpdateBrandStandardsMutation();

  const startEdit = () => {
    setDraft(JSON.stringify(brand.standards ?? {}, null, 2));
    setError(null);
    setEditing(true);
  };

  const submit = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError(t('brands.standards.invalidJson'));
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setError(t('brands.standards.mustBeObject'));
      return;
    }
    try {
      await save({ brandId: brand.id, body: { standards: parsed as Record<string, unknown> } }).unwrap();
      toast.success(t('brands.standards.saved'));
      setEditing(false);
    } catch (e) {
      setError(messageOf(e, t('brands.standards.saveFailed')));
    }
  };

  const hasStandards = brand.standards && Object.keys(brand.standards).length > 0;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <div>
          <CardTitle>{t('brands.standards.title')}</CardTitle>
          <CardDescription>{t('brands.standards.subtitle')}</CardDescription>
        </div>
        {canEdit && !editing && (
          <Button variant="outline" size="sm" onClick={startEdit}>
            {hasStandards ? t('common.edit') : t('brands.standards.add')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-3">
            <Field
              label={t('brands.standards.jsonLabel')}
              hint={t('brands.standards.jsonHint')}
              error={error ?? undefined}
            >
              <Textarea
                rows={16}
                className="font-mono text-xs"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                dir="ltr"
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submit} disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </div>
        ) : hasStandards ? (
          <pre className="scrollbar-thin max-h-96 overflow-auto rounded-lg bg-subtle p-4 text-xs" dir="ltr">
            {JSON.stringify(brand.standards, null, 2)}
          </pre>
        ) : (
          <EmptyState
            title={t('brands.standards.empty')}
            hint={canEdit ? t('brands.standards.emptyHint') : undefined}
            action={
              canEdit ? (
                <Button size="sm" onClick={startEdit}>
                  {t('brands.standards.add')}
                </Button>
              ) : undefined
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
